import { CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Popover, Typography } from 'antd'
import classNames from 'classnames'
import React, { useContext, useMemo } from 'react'
import { VerticalContext } from 'store/verticals/Context'
import { IRegistry } from 'store/verticals/types'
import { Table, TableProvider } from 'uikit/tables/default'
import { formattedDate } from 'utils/formatters'
import { columns } from './columns'
import { registryMatchesPopoverText } from './consts'
import { NoRegistryMatchesFound } from './noRegistryMatchesFound'
import styles from './registryMatches.module.scss'
import { RegistryMatch } from './types'
import { WhiteBoxWrapper } from '../../WhiteBoxWrapper_new/WhiteBoxWrapper_new'

const { Text } = Typography

export const RegistryMatches = () => {
  const { verticalEntity } = useContext(VerticalContext)

  const rows: RegistryMatch[] = useMemo(() => {
    return (
      verticalEntity?.overviewDetails?.registry?.map((item: IRegistry) => {
        const businessNamesArray = item.names.filter(el => el.key !== 'dba_name')
        const dbaObject = item.names.find(el => el.key === 'dba_name')

        const startDateFormatted = item?.startDate ? formattedDate(item.startDate, 'MMMM DD, YYYY') : 'N/A'
        const endDateFormatted = item?.endDate ? formattedDate(item.endDate, 'MMMM DD, YYYY') : 'Present'

        return {
          id: item.ids.map((idObj, index) => `${idObj.key}--${idObj.value}--${index}`).join('-'),
          regId: item.ids,
          businessNames: businessNamesArray.map(el => el.value),
          dba: dbaObject?.value || 'N/A',
          addresses: item?.addresses?.map(addressObj => addressObj.value),
          status: (() => {
            if (!item?.endDate) return 'Active'
            const endDate = new Date(item.endDate)
            const now = new Date()
            return endDate > now ? 'Active' : 'Inactive'
          })(),
          startDate: startDateFormatted,
          endDate: endDateFormatted,
          dateRange: `${startDateFormatted} - ${endDateFormatted}`,
        }
      }) || []
    )
  }, [verticalEntity?.overviewDetails?.registry])

  return (
    <>
      <WhiteBoxWrapper>
        <div className={styles.headerContainer}>
          <div className={styles.headerLeft}>
            {rows.length > 0 ? (
              <CheckCircleOutlined className={classNames(styles.headerIcon, styles.primatyColor)} />
            ) : (
              <CloseCircleOutlined className={styles.headerIcon} />
            )}
            <Text className={styles.headerTitle}>Registry Matches Found</Text>
            <span className={classNames(styles.matchesBadge, { [styles.noResults]: rows.length === 0 })}>
              {rows.length} {rows.length === 1 ? 'Match' : 'Matches'}
            </span>
          </div>
          <div className={styles.helpLinkContainer}>
            <Popover
              zIndex={10_000}
              content={<div className={styles.registryMatchesPopoverContent}>{registryMatchesPopoverText}</div>}
              placement='bottom'
            >
              <button className={styles.helpLink}>
                <QuestionCircleOutlined className={styles.helpIcon} />
                <span>How Registry Matching Works</span>
              </button>
            </Popover>
          </div>
        </div>

        {rows.length > 0 ? (
          <div className={styles.tableContainer}>
            <TableProvider columns={columns}>
              <Table<RegistryMatch>
                dataSource={rows}
                rowKey='id'
                pagination={{ pageSize: 2, showSizeChanger: false, hideOnSinglePage: true }}
                scroll={{ x: 'max-content' }}
              />
            </TableProvider>
          </div>
        ) : (
          <NoRegistryMatchesFound />
        )}
      </WhiteBoxWrapper>
    </>
  )
}
